import { Meteor } from 'meteor/meteor';
import { DeclareMethod, MethodDeclaration } from '/imports/methods';
import { Accounts, Roles } from '/imports/api/accounts';
import { validateString, validateUnique } from '/imports/validators';
import { AndroidDevice, IosDevice, ListMethodArg, ListMethodResponse, ModelClass, References } from '/imports/types';
import { pluck } from '/imports/utils';
import { strings } from '/imports/strings';

if (Meteor.isServer) {
  Meteor.users.createIndex({ email: 1 }, { unique: true, sparse: true });
  Meteor.users.createIndex({ phone: 1 }, { unique: true, sparse: true });
}

type Model = Meteor.User;
type Refs = References<Model>;

/**
 * Builds a Mongo query matching users by id, name, phone, or email.
 * @param search - free-text search term
 * @returns a Mongo selector ({} when search is empty)
 */
export const userSearchQuery = (search?: string) => {
  search = search?.trim();
  if (!search?.length) return {};
  return {
    $or: [
      { _id: search },
      { username: new RegExp(search, 'gi') },
      { firstName: new RegExp(search, 'gi') },
      { lastName: new RegExp(search, 'gi') },
      { phone: new RegExp(search, 'gi') },
      { phone: new RegExp(search, 'gi') },
      { email: new RegExp(search, 'gi') },
    ]
  };
};

export type ProfileUpdate = Partial<Pick<
  Meteor.User,
  '_id' | 'active' | 'username' | 'password' | 'firstName' | 'lastName' | 'avatar' |
  'email' | 'phone' | 'language' | 'timezone' | 'dateTimeFormat' | 'isSuperUser' | 'verified'
>>

/**
 * Validates a user profile update, enforcing required and unique fields.
 * @param profile - the profile fields to validate
 * @returns map of field name to validation error messages
 */
export const validateUserProfileUpdate = async (profile: ProfileUpdate) => {
  const errors: { [key: string]: string[] } = {};
  validateString(errors, profile, 'username', { required: true });
  await validateUnique(errors, profile, 'username', Meteor.users);
  const { password } = profile;
  password && validateString(errors, profile, 'password', { required: true });
  validateString(errors, profile, 'firstName');
  validateString(errors, profile, 'lastName');
  validateString(errors, profile, 'email');
  profile.email && await validateUnique(errors, profile, 'email', Meteor.users);
  validateString(errors, profile, 'phone');
  profile.phone && await validateUnique(errors, profile, 'phone', Meteor.users);
  validateString(errors, profile, 'language');
  return errors;
};

/**
 * Picks the admin-editable fields from a profile update.
 * @param profile - raw profile input
 * @returns profile limited to allowed fields
 */
export const cleanUserProfile = (profile: ProfileUpdate) => {
  return pluck(profile, [
    'active', 'username', 'firstName', 'lastName', 'email', 'phone', 'avatar',
    'language', 'timezone', 'dateTimeFormat', 'isSuperUser', 'verified',
  ]);
};

/**
 * Picks the self-editable fields from a profile update (excludes privileged fields).
 * @param profile - raw profile input
 * @returns profile limited to allowed fields
 */
export const cleanMyUserProfile = (profile: ProfileUpdate) => {
  return pluck(profile, [
    'active', 'username', 'firstName', 'lastName', 'email', 'phone', 'avatar',
    'language', 'timezone', 'dateTimeFormat',
  ]);
};

export const visibleUserFields = {
  active: 1,
  username: 1,
  isSuperUser: 1,
  orgId: 1,
  email: 1,
  phone: 1,
  firstName: 1,
  lastName: 1,
  language: 1,
  settings: 1,
  avatar: 1,
  timezone: 1,
  dateTimeFormat: 1,
  verified: 1,
  _name: 1,
};

export const listUsers = DeclareMethod<ListMethodArg<Meteor.User>, ListMethodResponse<Meteor.User>>('listUsers');
export const listActiveUsers = DeclareMethod<ListMethodArg<Meteor.User>, ListMethodResponse<Meteor.User>>('listActiveUsers');
export const getUser = DeclareMethod<string, Meteor.User>('getUser');
export const createNewUser = DeclareMethod<ProfileUpdate, string>('createNewUser');
export const signupUser = DeclareMethod<{ username: string, password: string, referralCode?: string }, string>('signupUser');
export const updateUser = DeclareMethod<ProfileUpdate, string>('updateUser');
export const updateUserProfile = DeclareMethod<ProfileUpdate>('updateUserProfile');
export const deleteUser = DeclareMethod<string>('deleteUser');
export const setOrgId = DeclareMethod<string>('setOrgId');
export const setSetting = DeclareMethod<{ key: string, value: any }>('setSetting');
export const sendPasswordResetLink = DeclareMethod<string>('sendPasswordResetLink');
export const sendEmailVerification = DeclareMethod<void>('sendEmailVerification');
export const resetUserPassword = DeclareMethod<{ password?: string, jwt?: string }>('resetUserPassword');
export const ensureAndroidDevice = DeclareMethod<AndroidDevice>('ensureAndroidDevice');
export const ensureIosDevice = DeclareMethod<IosDevice>('ensureIosDevice');
export const testPushNotification = DeclareMethod<string>('testPushNotification');
export const verifyEmailJwt = DeclareMethod<string | null, void>('verifyEmailJwt');
export const sendLoginLink = DeclareMethod<string | null, string>('sendLoginLink');
export const getUserLoginJwt = DeclareMethod<string, string>('getUserLoginJwt');
export const deleteMyUser = DeclareMethod<void, void>('deleteMyUser');

/**
 * Builds a human-readable display name for a user.
 * @param user - user with name/username fields
 * @returns formatted display name
 */
export const userDisplayName = <T extends { _id?: string, username?: string, firstName?: string, lastName?: string }>(user: T) => {
  if (user.firstName) return `${user.firstName} ${user.lastName || ''} (${user.username})`;
  return user.username || user._id || '';
};

export const modelClass: ModelClass<Model, Refs> = {
  collection: Meteor.users,
  editableFields: ['active', 'username', 'isSuperUser', 'email', 'phone', 'firstName', 'lastName', 'language', 'avatar', 'timezone', 'dateTimeFormat',],
  fieldLimits: {
    'dateTimeFormat': 64, 'email': 320, 'firstName': 256, 'language': 64, 'lastName': 256, 'phone': 64, 'timezone': 64,
    'username': 256,
  },
  listMethod: listUsers,
  getMethod: getUser,
  createMethod: createNewUser as MethodDeclaration<Partial<Meteor.User>, string>,
  updateMethod: updateUser,
  deleteMethod: deleteUser,
  pathPrefix: '/users',
  // icon: 'person' as IconKey,
  asString: (document: Model) => `${userDisplayName(document)} (${document.username})`,
  modelName: strings.User(),
  pluralName: strings.Users(),
  searchQuery: userSearchQuery,
  validate: validateUserProfileUpdate,
};

/**
 * Whether the user may list all users.
 * @param user - current user
 * @returns true if a super user
 */
export const canListUsers = (user?: Meteor.User | null) => {
  return user?.isSuperUser;
};

/**
 * Whether the user may list users for an org (super user or org admin).
 * @param user - current user
 * @param orgId - org to check; defaults to the user's org
 * @returns true if permitted
 */
export const canListAccountUsers = async (user?: Meteor.User | null, orgId?: string) => {
  if (!user) return false;
  if (user?.isSuperUser) return true;
  orgId = orgId || user.orgId;
  if (!orgId) return false;
  const orgUser = await Accounts.findOneAsync({ userId: user._id, orgId });
  if (orgUser?.roles?.includes(Roles.admin)) return true;
  return false;
};

/**
 * Resolves the current user's id and org id.
 * @returns { userId, orgId } for the logged-in user
 */
export const userAndOrgId = async () => {
  const user = await Meteor.userAsync();
  const userId = user?._id;
  const orgId = user?.orgId;
  return { userId, orgId };
};